import { useParams } from 'react-router-dom'
import { Linkedin } from 'lucide-react'
import Breadcrumb from '@/components/ui/Breadcrumb'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
// import StrategicPillars from '@/components/sections/StrategicPillars'
import RecentArticles from '@/components/sections/RecentArticles'
import Newsletter from '@/components/sections/Newsletter'
import { useMember } from '@/lib/api/hooks/member'
import getLinkedInUrl from '@/utils/helpers/getLinkedinUrl'

// Helper function to format member category to position
const formatMemberCategory = (category: string): string => {
  const categoryMap: Record<string, string> = {
    'PRESIDENT': 'President',
    'VICE_PRESIDENT': 'Vice President',
    'MEMBER': 'Member'
  }
  return categoryMap[category] || category
}


const Biography = () => {
  const { leaderId } = useParams<{ leaderId: string }>()
  const { data: memberResponse, isLoading, error } = useMember(leaderId)

  // Loading state
  if (isLoading) {
    return <LoadingSpinner fullScreen message="Loading biography..." />
  }

  // Error or not found state
  if (error || !memberResponse?.data) {
    return (
      <div className="bg-white min-h-screen">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-2xl font-normal font-pt-serif text-gray-900 mb-4">
              Leader Not Found
            </h1>
            <p className="text-lg font-satoshi text-gray-600">
              {error?.message || 'The requested biography could not be found.'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  const member = memberResponse.data

  const linkedInUrl = getLinkedInUrl(member.socialMediaLinks)

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'About Us', href: '/about' },
    { label: 'Leadership', href: '/about/leadership' },
    { label: member.fullName, href: `/about/leadership/${leaderId}/biography`, active: true }
  ]

  return (
    <div className="bg-white min-h-screen">
      {/* Breadcrumb */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 ">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
          {/* Left Content */}
          <div className="xl:col-span-8">
            {/* Leader Profile */}
            <div className="flex flex-col gap-10 mb-16">
              <div className="flex flex-col gap-6">
                <div className="w-72 h-72 relative">
                  <img
                    src={member.imageUrl}
                    alt={member.fullName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback image if loading fails
                      e.currentTarget.src = 'https://placehold.co/600x400/E5E7EB/6B7280?text=No+Image'
                    }}
                  />
                </div>
                
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col gap-1">
                    <h1 className="text-3xl md:text-4xl font-normal font-pt-serif text-gray-900 leading-tight">
                      {member.fullName}
                    </h1>
                    <p className="text-lg font-medium  text-gray-500 ">
                      {formatMemberCategory(member.memberCategory)}
                    </p>
                    {member.countryName && (
                      <p className="text-base font-normal  text-gray-600 ">
                        {member.countryName}
                      </p>
                    )}
                  </div>
                  
                  {linkedInUrl && (
                    <a 
                      href={linkedInUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-6 h-6 text-sky-700 hover:text-sky-800 transition-colors"
                    >
                      <Linkedin className="w-full h-full" />
                    </a>
                  )}
                </div>
              </div>

              {/* Biography Section */}
              {member.bio && (
                <div className="flex flex-col gap-6">
                  <h2 className="text-lg md:text-3xl font-normal font-pt-serif text-gray-900 leading-loose">
                    Biography
                  </h2>
                  
                  <div 
                    className="prose prose-lg max-w-none text-base font-normal font-satoshi text-gray-900 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: member.bio }}
                  />
                </div>
              )}

              {/* Newsletter Section */}
              <div className="flex flex-col gap-8 mt-16">
                <Newsletter />
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="xl:col-span-4 space-y-10">
            <RecentArticles />
            {/* <StrategicPillars /> */}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Biography